import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { cn } from "@/lib/utils";

// The Tapestry — each column is a day, each row is a habit-thread.
// Complete = full colour, rest = medium tint, missed = soft outline gap (never red).
export default function HealthHistory() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/health/history");
    setData(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-sm text-surface-onVariant">Weaving your tapestry…</div>;

  // Group days by ISO week for section headers
  const weeks = [];
  for (let i = 0; i < data.days.length; i += 7) weeks.push(data.days.slice(i, i + 7));

  return (
    <div data-testid="health-history" className="space-y-6">
      <div className="rounded-hero bg-surface-container p-6">
        <p className="text-sm text-surface-onVariant">
          Threads woven over the last {data.days.length} days. Full colour is a completed habit; softer tint is a rest day; a soft gap is a day it didn't happen — never a mark against you.
        </p>
      </div>

      <div className="rounded-hero bg-surface-container p-4 md:p-6 overflow-x-auto scrollbar-thin">
        <div className="min-w-[720px]">
          {/* Habit labels + rhythm */}
          <div className="grid gap-2" style={{ gridTemplateColumns: `160px repeat(${data.days.length}, minmax(14px, 1fr))` }}>
            <div />
            {data.days.map((d, i) => {
              const isMonday = new Date(d.date).getDay() === 1;
              return (
                <div key={i} className="text-[9px] text-surface-onVariant text-center tracking-wide" title={d.date}>
                  {isMonday ? new Date(d.date).getDate() : ""}
                </div>
              );
            })}
            {data.habits.map((h) => (
              <FragmentRow key={h.id} habit={h} days={data.days} />
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="rounded-hero bg-surface-container p-4 flex flex-wrap items-center gap-6 text-xs text-surface-onVariant">
        <span className="inline-flex items-center gap-2"><span className="w-4 h-4 rounded-sm bg-primary" /> Done</span>
        <span className="inline-flex items-center gap-2"><span className="w-4 h-4 rounded-sm bg-primary/40" /> Rest day</span>
        <span className="inline-flex items-center gap-2"><span className="w-4 h-4 rounded-sm border border-outline-variant bg-surface" /> Gap (no shame)</span>
      </div>
    </div>
  );
}

function FragmentRow({ habit, days }) {
  return (
    <>
      <div className="flex items-center justify-between pr-3 py-1">
        <div className="text-sm font-medium text-surface-on truncate">{habit.name}</div>
        <div className="text-[10px] text-surface-onVariant tabular-nums shrink-0" data-testid={`tapestry-rhythm-${habit.id}`}>{habit.rhythm_30d}%</div>
      </div>
      {days.map((d, i) => {
        const state = d.habit_states[habit.id];
        let bg = "transparent";
        let border = "1px solid #CAC4D0";
        if (state === "done") { bg = habit.color; border = "none"; }
        else if (state === "rest") { bg = habit.color + "55"; border = "none"; }
        else if (state === "skipped") { bg = habit.color + "22"; border = "none"; }
        // else: no log → soft outline (already default)
        return (
          <div
            key={i}
            data-testid={`tapestry-cell-${habit.id}-${d.date}`}
            title={`${habit.name} · ${d.date}${state ? " · " + state : ""}`}
            className={cn("h-5 rounded-sm transition-colors duration-200 ease-md", !state && "opacity-70")}
            style={{ background: bg, border: state ? "none" : border }}
          />
        );
      })}
    </>
  );
}
